import NextLazyImage from "@/components/Core/NextLazyImage";
import mq from "@/utils/mq";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

const IconItem = styled(motion.div)`
  width: 100%;
  height: 100%;
  position: relative;
  padding: 52px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mq.mobile(css`
    padding: 24px;
    flex: 1;
    * > img {
      position: relative !important;
      max-width: 100px;
    }
  `)};
`;

interface Icon {
  filename: string;
  alt: string;
}

interface BentoIconGridProps {
  item: {
    icons: Icon[];
  };
}

const MIN_ROTATION_INTERVAL = 4000;
const MAX_ROTATION_INTERVAL = 15000;
const SLOTS_COUNT = 4;

function getRandomInterval(): number {
  return Math.random() * (MAX_ROTATION_INTERVAL - MIN_ROTATION_INTERVAL) + MIN_ROTATION_INTERVAL;
}

function getRandomIcon(icons: Icon[], exclude: Icon[] = []): Icon | null {
  const availableIcons = icons.filter((icon) => !exclude.some((excluded) => excluded.filename === icon.filename));
  if (availableIcons.length === 0) return icons[Math.floor(Math.random() * icons.length)];
  return availableIcons[Math.floor(Math.random() * availableIcons.length)];
}

function BentoIconGrid({ item }: BentoIconGridProps) {
  const [slotIcons, setSlotIcons] = useState<(Icon | null)[]>(Array(SLOTS_COUNT).fill(null));

  useEffect(() => {
    if (!item.icons || item.icons.length === 0) return;

    const timeouts: NodeJS.Timeout[] = [];

    const initializeSlot = (slotIndex: number, initialDelay: number = 0) => {
      const scheduleRotation = () => {
        const timeout = setTimeout(() => {
          setSlotIcons((prev) => {
            const newSlotIcons = [...prev];
            const excludeIcons = prev.filter((icon): icon is Icon => icon !== null);
            newSlotIcons[slotIndex] = getRandomIcon(item.icons, excludeIcons);
            return newSlotIcons;
          });
          scheduleRotation();
        }, initialDelay + getRandomInterval());

        timeouts.push(timeout);
      };

      setSlotIcons((prev) => {
        const newSlotIcons = [...prev];
        const excludeIcons = prev.filter((icon): icon is Icon => icon !== null);
        newSlotIcons[slotIndex] = getRandomIcon(item.icons, excludeIcons);
        return newSlotIcons;
      });

      scheduleRotation();
    };

    for (let i = 0; i < SLOTS_COUNT; i++) {
      const staggerDelay = i * 500;
      initializeSlot(i, staggerDelay);
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [item.icons]);

  if (!item.icons || item.icons.length === 0) return null;

  return (
    <>
      {Array.from({ length: SLOTS_COUNT }).map((_, index) => (
        <AnimatePresence key={`slot-${index}`} mode='wait'>
          {slotIcons[index] && (
            <IconItem
              key={`${slotIcons[index]!.filename}-${index}`}
              initial={{ opacity: 0, scale: 1, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1, y: 30 }}
              transition={{
                duration: 0.2,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <Image
                  src={slotIcons[index]!.filename}
                  alt={slotIcons[index]!.alt}
                  fill
                  style={{ objectFit: "contain" }}
                />
              </div>
            </IconItem>
          )}
        </AnimatePresence>
      ))}
    </>
  );
}

export default BentoIconGrid;
